import type { EventPayloadMap, PosEvent } from './events'
import type { EventHandler, HandlerResult } from './types'

/**
 * Жёсткий таймаут безопасности на выполнение одного хендлера (мс).
 *
 * Если плагин (например, Честный Знак) завис и не ответил за это время,
 * шина НЕ блокирует кассу: такой хендлер трактуется как «нет вето»
 * ({ ok: true }, fail-open по таймауту) с предупреждением в консоль.
 * Это сознательное исключение из общего fail-safe правила: зависший плагин
 * не должен намертво блокировать кнопку «ОПЛАТИТЬ».
 */
export const HANDLER_TIMEOUT_MS = 3000

/**
 * Таймаут для интерактивных событий, где ответ зависит от ввода человеком
 * (PRE_SALE → ввод кодов маркировки кассиром). 2 минуты, fail-CLOSED: по
 * истечении возвращаем veto, а не одобрение — продажа маркируемого товара
 * НЕ должна проходить без фактического ввода марки.
 */
export const INTERACTIVE_HANDLER_TIMEOUT_MS = 120000

/**
 * Типизированная асинхронная шина событий Ядра.
 *
 * - on/off/emit строго типизированы через EventPayloadMap.
 * - emit дожидается ВСЕХ хендлеров (включая async) и агрегирует их HandlerResult.
 * - Агрегация с veto: если хотя бы один хендлер вернул { ok: false },
 *   общий результат — { ok: false } с первым vetoReason (блокировка операции).
 * - Исключение в хендлере трактуется как veto (fail-safe для фискальных операций).
 * - Зависание хендлера дольше HANDLER_TIMEOUT_MS трактуется как { ok: true }
 *   (fail-open), чтобы не блокировать интерфейс кассы.
 */
export class EventBus {
  private readonly handlers = new Map<PosEvent, Set<EventHandler<PosEvent>>>()

  /** Подписаться на событие. Возвращает функцию отписки. */
  on<E extends PosEvent>(event: E, handler: EventHandler<E>): () => void {
    let set = this.handlers.get(event)
    if (!set) {
      set = new Set()
      this.handlers.set(event, set)
    }
    set.add(handler as EventHandler<PosEvent>)
    return () => this.off(event, handler)
  }

  /** Отписаться от события. */
  off<E extends PosEvent>(event: E, handler: EventHandler<E>): void {
    this.handlers.get(event)?.delete(handler as EventHandler<PosEvent>)
  }

  /**
   * Эмитировать событие и дождаться всех хендлеров.
   * @returns агрегированный результат (важно для PRE_SALE / PAYMENT_REQUESTED).
   */
  async emit<E extends PosEvent>(
    event: E,
    payload: EventPayloadMap[E]
  ): Promise<HandlerResult> {
    const set = this.handlers.get(event)
    if (!set || set.size === 0) {
      return { ok: true }
    }

    const results = await Promise.all(
      [...set].map((handler) => this.runHandler(event, handler, payload))
    )

    const veto = results.find((r) => !r.ok)
    return veto ?? { ok: true }
  }

  /**
   * Выполнить один хендлер с жёстким таймаутом безопасности.
   *
   * - штатный возврат → его HandlerResult (void трактуется как { ok: true });
   * - исключение → veto (fail-safe для фискальных операций);
   * - превышение HANDLER_TIMEOUT_MS → { ok: true } (fail-open), чтобы зависший
   *   плагин не блокировал кассу; пишем предупреждение в консоль.
   */
  private runHandler<E extends PosEvent>(
    event: E,
    handler: EventHandler<PosEvent>,
    payload: EventPayloadMap[E]
  ): Promise<HandlerResult> {
    let timer: ReturnType<typeof setTimeout> | undefined

    // PRE_SALE интерактивен (ввод марки человеком): длинный таймаут и
    // fail-CLOSED. Остальные фоновые события — короткий таймаут и fail-open.
    const isInteractive = event === 'PRE_SALE'
    const timeoutMs = isInteractive ? INTERACTIVE_HANDLER_TIMEOUT_MS : HANDLER_TIMEOUT_MS

    const timeout = new Promise<HandlerResult>((resolve) => {
      timer = setTimeout(() => {
        if (isInteractive) {
          console.warn(
            `[bus] хендлер события "${event}" не ответил за ${timeoutMs} мс — ` +
              `блокируем операцию (fail-closed)`
          )
          resolve({
            ok: false,
            vetoReason: 'Превышено время ожидания проверки маркировки'
          })
        } else {
          console.warn(
            `[bus] хендлер события "${event}" не ответил за ${timeoutMs} мс — ` +
              `пропускаем (fail-open, { ok: true })`
          )
          resolve({ ok: true })
        }
      }, timeoutMs)
    })

    const run = (async (): Promise<HandlerResult> => {
      try {
        const res = await handler(payload)
        return res ?? { ok: true }
      } catch (err) {
        return {
          ok: false,
          vetoReason: err instanceof Error ? err.message : String(err)
        }
      }
    })()

    return Promise.race([run, timeout]).finally(() => {
      if (timer !== undefined) {
        clearTimeout(timer)
      }
    })
  }

  /** Количество подписчиков на событие (для диагностики/статусов). */
  listenerCount(event: PosEvent): number {
    return this.handlers.get(event)?.size ?? 0
  }

  /** Полностью очистить подписки (используется при dispose). */
  clear(): void {
    this.handlers.clear()
  }
}
